import { useState } from 'react';
import {
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    TextField,
} from '@mui/material';

import { useGiftAdmin } from '../../hooks/useGiftApi';
import { JiraSubtask } from '../../hooks/useJiraApi';

import { assert } from '../../utils/assert';
import { toStartOfDayIso } from '../utils/dates';

interface Props {
    subtask: JiraSubtask;
    open: boolean;
    onClose: () => void;
}

export const ScheduleGiftDialog = ({ subtask, open, onClose }: Props) => (
    <Dialog open={open} onClose={onClose}>
        {open && <Form key={subtask.key} subtask={subtask} onClose={onClose} />}
    </Dialog>
);

const Form = ({
    subtask,
    onClose,
}: {
    subtask: JiraSubtask;
    onClose: () => void;
}) => {
    const { createGift, isSaving } = useGiftAdmin();

    const [giftName, setGiftName] = useState(subtask.summary);

    const handleScheduleGift = async () => {
        assert(subtask.startTime, 'startTime is undefined');
        assert(subtask.endTime, 'endTime is undefined');
        await createGift(
            crypto.randomUUID(),
            {
                name: giftName,
                wrapperGameElementId: subtask.wrappingGameElementId || '',
                contents: subtask.contentGameElementId
                    ? [subtask.contentGameElementId]
                    : [],
                tutorial: false,
                defaultClaimed: false,
                minimumVersion: null,
                jiraIssueKey: subtask.key,
            },
            {
                startDate: toStartOfDayIso(subtask.startTime),
                endDate: toStartOfDayIso(subtask.endTime),
            },
        );
        onClose();
    };

    return (
        <>
            <DialogTitle>Schedule gift</DialogTitle>
            <DialogContent>
                <DialogContentText sx={{ mb: 2 }}>
                    Create a scheduled gift from <strong>{subtask.key}</strong>.
                    You can adjust the name below; dates and elements come from
                    the Jira subtask.
                </DialogContentText>
                <TextField
                    label="Gift name"
                    value={giftName}
                    onChange={(e) => setGiftName(e.target.value)}
                    fullWidth
                    autoFocus
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button
                    variant="contained"
                    onClick={handleScheduleGift}
                    disabled={isSaving}
                >
                    {isSaving ? (
                        <CircularProgress size={16} />
                    ) : (
                        'Yes, schedule'
                    )}
                </Button>
            </DialogActions>
        </>
    );
};
